import * as PIXI from 'pixi.js';
import { logger } from '@/engine/logging';
import { AssetLoader } from '@/engine/assets/loaders/AssetLoader';
import { ASTEROID_SVG_PATHS, ORE_ICON_SVG_PATHS, RESOURCE_ICON_SVG_PATHS } from '@/assets/svg';
import { getAllItems } from '@/generated/items';
import { SPRITE_PATHS } from '@/assets/sprites';
import { getItemIconUrl } from '@/assets/icons';

/**
 * Сервис для загрузки специфичных для игры ресурсов.
 * Использует универсальный AssetLoader из движка.
 */
export class GameAssetService {
  private assetLoader: AssetLoader;

  constructor(assetLoader: AssetLoader) {
    this.assetLoader = assetLoader;
  }

  /**
   * Регистрация всех игровых ресурсов.
   */
  public async registerAssets(): Promise<void> {
    // Регистрируем астероиды
    this.assetLoader.registerTextureGroup(ASTEROID_SVG_PATHS, key => `asteroid-${key}`);

    // Регистрируем иконки руды
    this.assetLoader.registerTextureGroup(ORE_ICON_SVG_PATHS, key => `ore-icon-${key}`);

    // Регистрируем иконки ресурсов
    this.assetLoader.registerTextureGroup(RESOURCE_ICON_SVG_PATHS, key => `${key}-icon`);

    // Регистрируем спрайты как texture group
    this.assetLoader.registerTextureGroup(SPRITE_PATHS, key => key);

    // Регистрируем PNG иконки из папки icons/
    await this.registerItemIcons();

    logger.info('📋 GameAssetService: игровые ресурсы зарегистрированы', { source: 'game' });
  }

  /**
   * Загрузка всех зарегистрированных игровых текстур.
   */
  public async loadTextures(): Promise<void> {
    try {
      // Загружаем астероиды
      await this.assetLoader.loadTextureGroup(ASTEROID_SVG_PATHS, key => `asteroid-${key}`);

      // Загружаем иконки руды
      await this.assetLoader.loadTextureGroup(ORE_ICON_SVG_PATHS, key => `ore-icon-${key}`);

      // Загружаем иконки ресурсов
      await this.assetLoader.loadTextureGroup(RESOURCE_ICON_SVG_PATHS, key => `${key}-icon`);

      // Загружаем спрайты как texture group
      await this.assetLoader.loadTextureGroup(SPRITE_PATHS, key => key);

      // Загружаем PNG иконки предметов
      await this.loadItemIcons();

      logger.info('✅ GameAssetService: все игровые текстуры загружены!', { source: 'game' });
    } catch (error) {
      logger.error('❌ GameAssetService: Ошибка загрузки текстур:', error as Error, {
        source: 'game',
      });
      throw error;
    }
  }

  /**
   * Регистрация PNG иконок предметов
   */
  private async registerItemIcons(): Promise<void> {
    const items = getAllItems();

    const iconSet = new Set<string>();
    for (const item of items) {
      if (item.icon && item.icon.startsWith('icons/')) {
        const iconName = item.icon.replace('icons/', '').replace('.png', '');
        iconSet.add(iconName);
      }
    }

    for (const iconName of Array.from(iconSet)) {
      // Используем правильный URL для продакшена
      const iconUrl = getItemIconUrl(`icons/${iconName}.png`);
      PIXI.Assets.add({ alias: `item-${iconName}`, src: iconUrl });
    }

    logger.info('📋 GameAssetService: PNG иконки предметов зарегистрированы', {
      count: iconSet.size,
      icons: Array.from(iconSet),
      source: 'game',
    });
  }

  /**
   * Загрузка PNG иконок предметов
   */
  private async loadItemIcons(): Promise<void> {
    const items = getAllItems();

    const iconSet = new Set<string>();
    for (const item of items) {
      if (item.icon && item.icon.startsWith('icons/')) {
        const iconName = item.icon.replace('icons/', '').replace('.png', '');
        iconSet.add(iconName);
      }
    }

    for (const iconName of Array.from(iconSet)) {
      const assetName = `item-${iconName}`;
      await this.assetLoader.loadTexture(assetName);
    }
  }
}
